import * as React from 'react';

import { darken } from 'polished';
import styled from 'styled-components';

import { COLOR, FONT_SIZE, RADIUS, SPACING } from '../../packages/constants';

interface TabsProps {
    children: React.ReactNode;
    onClick?: (e: any) => void;
    className?: string;
}

interface TabListContainerProps {
    children: React.ReactNode;
}

interface TabListProps {
    children: React.ReactNode;
    spacingRight?: number;
}

interface TabListItemProps {
    children: React.ReactNode;
    className?: string;
}

interface TabTitleProps {
    children: React.ReactNode;
    onClick: (e: any) => void;
    selected: boolean;
    dataId: string;
    firstTab?: boolean;
    lastTab?: boolean;
    title: string;
}

interface TabContentProps {
    children: React.ReactNode;
    selected: boolean;
    className?: string;
}

interface TabListStyleProps {
    spacingRight?: number; // this number would typically be determined by the width of an 'add tab' button occupying the space if there is one
}

interface TabTitleStyleProps {
    selected: boolean;
    firstTab?: boolean;
    lastTab?: boolean;
}

const StyledTabs = styled.div``;
const StyledList = styled.ul<TabListStyleProps>`
    display: inline-flex;
    max-width: calc(100% - ${(props) => (props.spacingRight ? props.spacingRight : `0`)}px);
    overflow: hidden;
`;
const StyledTabListContainer = styled.div`
    overflow: hidden;
    margin-bottom: -1px;
`;

const StyledListItem = styled.li`
    &:not(:first-child) {
        margin-left: -1px;
    }
`;
const StyledTitle = styled.button<TabTitleStyleProps>`
    margin: 0px;
    border-top: 1px solid ${COLOR.MAIN.MEDIUM_GREY};
    border-top-left-radius: ${(props) => (props.firstTab ? RADIUS.FORM_FIELD : `0`)};
    border-top-right-radius: ${(props) => (props.lastTab ? RADIUS.FORM_FIELD : `0`)};
    border-right: 1px solid ${COLOR.MAIN.MEDIUM_GREY};
    border-bottom: ${(props) =>
        props.selected ? `1px solid ${COLOR.PRIMARY.WHITE}` : `1px solid ${COLOR.MAIN.MEDIUM_GREY}`};
    border-left: 1px solid ${COLOR.MAIN.MEDIUM_GREY};
    font-size: ${FONT_SIZE.X_SMALL};
    font-weight: 700;
    line-height: 20px;
    padding: ${SPACING.X_SMALL} ${SPACING.MEDIUM};
    background-color: ${COLOR.PRIMARY.WHITE};
    color: ${(props) => (props.selected && props.theme ? props.theme.main : COLOR.MAIN.LABEL_TEXT)};
    cursor: ${(props) => (props.selected ? 'default' : 'pointer')};
    max-width: 130px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover {
        background-color: ${(props) => (props.selected ? COLOR.PRIMARY.WHITE : darken(0.1, COLOR.PRIMARY.WHITE))};
    }
`;
const StyledContent = styled.div<TabContentProps>`
    background-color: ${COLOR.PRIMARY.WHITE};
    border: 1px solid ${COLOR.MAIN.MEDIUM_GREY};
    border-top-right-radius: ${RADIUS.FORM_FIELD};
    border-bottom-left-radius: ${RADIUS.FORM_FIELD};
    border-bottom-right-radius: ${RADIUS.FORM_FIELD};
    padding: ${SPACING.MEDIUM} ${SPACING.SMALL};
    display: ${(props) => (props.selected ? 'block' : 'none')};
`;

const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(({ children, ...props }, ref) => (
    <StyledTabs ref={ref} {...props}>
        {children}
    </StyledTabs>
));
Tabs.displayName = 'Tabs';

const TabListContainer = ({ children, ...props }: TabListContainerProps) => (
    <StyledTabListContainer {...props}>{children}</StyledTabListContainer>
);
const TabList = ({ children, ...props }: TabListProps) => <StyledList {...props}>{children}</StyledList>;
const TabListItem = ({ children, ...props }: TabListItemProps) => (
    <StyledListItem {...props}>{children}</StyledListItem>
);

const TabTitle = React.forwardRef<HTMLButtonElement, TabTitleProps>(
    ({ children, firstTab, lastTab, selected, dataId, title, ...props }, ref) => (
        <StyledTitle
            firstTab={firstTab}
            lastTab={lastTab}
            type={'button'}
            data-test-id={dataId}
            selected={selected}
            title={title}
            ref={ref}
            {...props}
        >
            {children}
        </StyledTitle>
    ),
);
TabTitle.displayName = 'TabTitle';

const TabContent = React.forwardRef<HTMLDivElement, TabContentProps>(({ children, selected, ...props }, ref) => (
    <StyledContent selected={selected} ref={ref} {...props}>
        {children}
    </StyledContent>
));
TabContent.displayName = 'TabContent';

export default Tabs;
export { TabListContainer, TabList, TabListItem, TabTitle, TabContent };
